// visits.service.ts
import { apiClient } from '../api-client';
import { ApiResponse, ApiError } from '../../dtos/common/api.dto';
import { Visit, CancelVisitRequest, EndVisitSignoff } from '../../dtos/patientmr/visit.dto';
import { Offline } from '../offline/offlineServices';
import { OfflineMode, runWithOfflineBranch } from '../offline/offlineMode';
import { isOfflineError, isOnline } from '../offline/network';
import { ulid } from '../offline/ids';
import type { LocalVisit } from '../offline/repos/visitRepo';

/** Phase 16C: a local visit in the Visit shape the screens use (`syncState` marks "Not synced"). */
export type VisitWithSync = Visit & { syncState?: LocalVisit['syncState']; origin?: LocalVisit['origin']; scheduleId?: string; evvRequired?: boolean; gpsDenied?: boolean };

export function localVisitToVisit(v: LocalVisit): VisitWithSync {
  const status: Visit['status'] = v.lifecycle === 'CANCELLED' ? 'CANCELLED' : v.lifecycle === 'ENDED' ? 'DOCPENDING' : 'INPROGRESS';
  return {
    id: v.visitId,
    visitId: v.visitId,
    patientId: v.patientId,
    providerId: '',
    tenantId: '',
    startTime: v.startedAt,
    endTime: v.endedAt,
    status,
    discipline: v.discipline,
    patientName: v.patientName,
    serviceType: v.serviceType,
    chartingStatus: v.chartingStatus,
    evvRequired: v.evvRequired,
    createdAt: v.startedAt,
    updatedAt: v.updatedAt,
    syncState: v.syncState,
    origin: v.origin,
    scheduleId: v.scheduleId,
    gpsDenied: v.gpsDenied,
  };
}

/** INPROGRESS (API) or IN_PROGRESS (older responses). */
export const isVisitInProgress = (v: Pick<Visit, 'status'> | null | undefined): boolean =>
  String(v?.status || '').replace('_', '').toUpperCase() === 'INPROGRESS';

/** Keep a server visit on an enrolled device so it can be ended or charted without signal. */
async function cacheServerVisit(visit: Visit, lifecycle: LocalVisit['lifecycle']): Promise<void> {
  if (!OfflineMode.enabled()) return;
  const raw = visit as Visit & { visitId?: string; scheduleId?: string };
  const visitId = raw.id || raw.visitId;
  if (!visitId) return;
  await Offline.visits.cacheServer({
    visitId,
    patientId: visit.patientId,
    lifecycle,
    startedAt: visit.startTime || '',
    endedAt: visit.endTime,
    discipline: visit.discipline,
    serviceType: visit.serviceType,
    chartingStatus: visit.chartingStatus || 'DRAFT',
    evvRequired: visit.evvRequired,
    patientName: visit.patientName,
    scheduleId: raw.scheduleId,
  }).catch(() => undefined);
}

/**
 * Response interface for paginated visit history
 */
export interface VisitHistoryResponse {
  visits: Visit[];
  paginationToken?: string;
  hasMore: boolean;
  totalCount?: number;
}

/**
 * Backend response structure for visit history
 */
interface VisitHistoryBackendResponse {
  visits: any[];
  paginationInfo?: {
    nextToken?: string;
    hasMoreItems?: boolean;
    totalCount?: number;
  };
  paginationToken?: string;
  hasMore?: boolean;
  totalCount?: number;
}

/**
 * Filter options for visit history
 */
export interface VisitHistoryFilters {
  startDate?: string; // YYYY-MM-DD format
  endDate?: string; // YYYY-MM-DD format
  status?: string; // VISIT_STATUS
  hipaaAccessReason?: string;
}

/**
 * Fetch visit history for a specific patient with pagination support
 */
export const fetchVisitHistory = async (
  patientId: string,
  limit: number = 5,
  paginationToken?: string,
  filters?: VisitHistoryFilters
): Promise<VisitHistoryResponse> => {
  // Phase 16C: offline on an enrolled phone, history is not stored on the device (the workspace
  // charts from the visit packet), so the list is empty instead of an error
  if (OfflineMode.enabled() && !isOnline()) return { visits: [], hasMore: false };
  try {
    // Build query parameters
    const params: Record<string, any> = { limit };

    if (paginationToken) {
      params.paginationToken = paginationToken;
    }

    // Add filter parameters
    if (filters) {
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.hipaaAccessReason) {
        params.hipaaAccessReason = filters.hipaaAccessReason;
      }
    }

    const response: ApiResponse<VisitHistoryBackendResponse> = await apiClient.get(
      `/patientmr/${patientId}/visits`,
      params
    );

    if (response.success && response.data) {
      const responseData = response.data;

      // Handle the response structure
      let visits: any[] = [];
      let nextPaginationToken: string | undefined;
      let hasMore = false;
      let totalCount: number | undefined;

      visits = responseData.visits || [];

      // Check for pagination info in the response
      if (responseData.paginationInfo) {
        nextPaginationToken = responseData.paginationInfo.nextToken;
        hasMore = responseData.paginationInfo.hasMoreItems || false;
        totalCount = responseData.paginationInfo.totalCount;
      } else {
        // Fallback to old structure
        nextPaginationToken = responseData.paginationToken;
        hasMore = responseData.hasMore || false;
        totalCount = responseData.totalCount;
      }

      // Transform the visit data to match the expected Visit interface
      const transformedVisits = visits.map((visit: any) => ({
        id: visit.visitId || visit.id,
        patientId: visit.patientId,
        providerId: visit.providerId,
        tenantId: visit.tenantId || '', // Default if not provided
        startTime: visit.startTime,
        endTime: visit.endTime,
        authorizationId: visit.priorAuthorizationId || visit.authorizationId,
        authorizationNumber: visit.authorizationNumber,
        notes: visit.notes,
        signature: visit.signature,
        createdAt: visit.createdAt || visit.startTime, // Use startTime as fallback
        updatedAt: visit.updatedAt || visit.endTime || visit.startTime, // Use endTime or startTime as fallback
        status: visit.status,
        // Additional properties from the API response
        visitDate: visit.visitDate,
        discipline: visit.discipline,
        patientName: visit.patientName,
        providerName: visit.providerName,
        chartingStatus: visit.chartingStatus || undefined,
        maxIncidentSeverity: visit.maxIncidentSeverity || undefined
      }));

      return {
        visits: transformedVisits,
        paginationToken: nextPaginationToken,
        hasMore,
        totalCount
      };
    }

    throw new Error(response.error?.message || 'Failed to fetch visit history');
  } catch (error) {
    console.error(`Error fetching visit history for patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Fetch current active visit for a patient and provider
 */
export const fetchCurrentVisit = async (patientId: string): Promise<Visit | null> => {
  try {
    const response: ApiResponse<Visit> = await apiClient.get(
      `/patientmr/${patientId}/visits/provider`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to fetch current visit');
  } catch (error) {
    console.error(`Error fetching current visit for patient ${patientId}:`, error);

    // If it's a 404 (NOT_FOUND), we'll return null to indicate no active visit
    if (error instanceof ApiError && error.code === 'NOT_FOUND') {
      return null;
    }
    throw error;
  }
};

/**
 * Get visit by ID
 */
export const fetchVisitById = async (patientId: string, visitId: string): Promise<Visit> => {
  try {
    const response: ApiResponse<Visit> = await apiClient.get(
      `/patientmr/${patientId}/visits/${visitId}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to fetch visit');
  } catch (error) {
    console.error(`Error fetching visit ${visitId} for patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Payload interface for starting a visit
 */
export interface StartVisitPayload {
  patientId: string;
  discipline: string;
  /** VIS-04: null when the device gave no fix; never 0,0 */
  location: {
    latitude: number;
    longitude: number;
  } | null;
  locationUnavailable?: boolean;
  /** Phase 9B: the shift this visit works (the server also auto-matches) */
  scheduleId?: string;
  /** Initial / Evaluation / Routine … (VIS-03 checks pending patients) */
  visitType: string;
  startTime: string;
  notes?: string;
  authorizationId?: string;
  authorizationNumber?: string;
  isOverride?: boolean;
  overrideReason?: string;
  evvClockInFlag?: 'ok' | 'missed_prompt';
  /** R-177: RN/LVN visit type (decides EVV: PDN optional, skilled nursing visit required) */
  serviceType?: 'PDN' | 'SKILLED_NURSING_VISIT';
}

/** Phase 18 M4 adds the signer identity and the "no care provided" path (EndVisitSignoff). */
export interface EndVisitPayload extends EndVisitSignoff {
  evvClockOutFlag?: 'ok' | 'missed_prompt';
  location?: { latitude: number; longitude: number };
}

/**
 * Start a new visit
 * @param payload The payload containing visit details
 * @returns A Promise that resolves to a Visit object
 */
export interface StartVisitContext {
  /** For the offline "In visit" bar */
  patientName?: string;
  /** From the visit packet (HHAX prompt applies) */
  evvRequired?: boolean;
}

export const startVisit = async (payload: StartVisitPayload, context: StartVisitContext = {}): Promise<Visit> => {
  // Phase 16C: client ULID as the visit id and the Idempotency-Key, so a retry after a timeout
  // (or the queued op) never creates a second visit
  const visitId = ulid();
  const startLocal = async () => localVisitToVisit(await Offline.local.startVisit({
    visitId,
    patientId: payload.patientId,
    patientName: context.patientName,
    payload: { ...payload },
    scheduleId: payload.scheduleId,
    evvRequired: context.evvRequired,
    coords: payload.location,
    gpsDenied: payload.location === null,
  }));
  return runWithOfflineBranch({
    visitId: null,
    local: startLocal,
    online: async () => {
      try {
        const response: ApiResponse<Visit> = await apiClient.post(
          `/patientmr/${payload.patientId}/visits`,
          payload,
          // 16B: the visit id defaults to the Idempotency-Key (a duplicate answers 200)
          { idempotencyKey: visitId }
        );
        if (response.success && response.data) {
          await cacheServerVisit({ ...response.data, id: response.data.id || visitId }, 'IN_PROGRESS');
          return response.data;
        }
        throw new Error(response.error?.message || 'Failed to start visit');
      } catch (error) {
        if (!isOfflineError(error)) console.error(`Error starting visit for patient ${payload.patientId}`);
        throw error;
      }
    },
  });
};

/**
 * End a visit.
 * Logs IDs only: the payload carries the patient signature and location (PHI).
 */
export const endVisit = async (patientId: string, visitId: string, endData: EndVisitPayload): Promise<Visit> => {
  if (!visitId) {
    throw new Error('Visit ID is required to end a visit');
  }
  const key = ulid();
  return runWithOfflineBranch({
    visitId,
    local: async () => localVisitToVisit(await Offline.local.endVisit(patientId, visitId, endData)),
    online: async () => {
      try {
        const response: ApiResponse<Visit> = await apiClient.post(`/patientmr/${patientId}/visits/${visitId}/end`, endData, { idempotencyKey: key });
        if (response.success && response.data) {
          await cacheServerVisit({ ...response.data, id: response.data.id || visitId }, 'ENDED');
          return response.data;
        }
        throw new Error(response.error?.message || 'Failed to end visit');
      } catch (error) {
        if (!isOfflineError(error)) console.error(`Error ending visit ${visitId}`);
        throw error;
      }
    },
  });
};

/** Some responses carry the visit id as visitId / _id / visit_id; normalize it to `id`. */
const withVisitId = (visit: Visit): Visit | null => {
  const raw = visit as Visit & { visitId?: string; _id?: string; visit_id?: string };
  const id = raw.id || raw.visitId || raw._id || raw.visit_id;
  return id ? { ...visit, id } : null;
};

/**
 * Get active visit for any patient (provider-wide)
 * @returns A Promise that resolves to a Visit object or null if no active visit exists
 */
export const getProviderActiveVisit = async (): Promise<Visit | null> => {
  // Phase 16C: the local active visit first (offline start not synced yet, or no signal)
  if (OfflineMode.enabled()) {
    const local = await Offline.local.getActiveVisit().catch(() => null);
    if (local && (local.syncState !== 'synced' || !isOnline())) return localVisitToVisit(local);
    if (!isOnline()) return null;
    const server = await getProviderActiveVisitOnline();
    if (server) await cacheServerVisit(server, 'IN_PROGRESS');
    else if (local) await Offline.visits.put({ ...local, lifecycle: 'ENDED' }).catch(() => undefined); // ended elsewhere
    return server;
  }
  return getProviderActiveVisitOnline();
};

const getProviderActiveVisitOnline = async (): Promise<Visit | null> => {
  try {
    const response: ApiResponse<Visit> = await apiClient.get(
      // The route is /patientmr/{patientId}/visits/provider/active; the patient segment is unused
      '/patientmr/me/visits/provider/active'
    );
    if (response.success && response.data) {
      return withVisitId(response.data);
    }
    throw new Error(response.error?.message || 'Failed to fetch provider active visit');
  } catch (error) {
    // If it's a 404 (NOT_FOUND), we'll return null to indicate no active visit
    if (error instanceof ApiError && error.code === 'NOT_FOUND') {
      return null;
    }
    console.error('Error fetching provider active visit');
    throw error;
  }
};

/**
 * Cancel a visit
 * @param patientId The patient ID
 * @param visitId The visit ID to cancel
 * @param payload Cancellation reason and optional note
 * @returns A Promise that resolves to the updated Visit object
 */
export const cancelVisit = async (
  patientId: string,
  visitId: string,
  payload: CancelVisitRequest
): Promise<Visit> => {
  return runWithOfflineBranch({
    visitId,
    local: async () => localVisitToVisit(await Offline.local.cancelVisit(patientId, visitId, { ...payload })),
    online: async () => {
      try {
        const response: ApiResponse<Visit> = await apiClient.post(
          `/patientmr/${patientId}/visits/${visitId}/cancel`,
          payload
        );
        if (response.success && response.data) return response.data;
        throw new Error(response.error?.message || 'Failed to cancel visit');
      } catch (error) {
        if (!isOfflineError(error)) console.error(`Error cancelling visit ${visitId} for patient ${patientId}`);
        throw error;
      }
    },
  });
};

/**
 * Get active visit for a provider
 * @param patientId The patient ID
 * @returns A Promise that resolves to a Visit object or null if no active visit exists
 */
export const getActiveVisit = async (patientId: string): Promise<Visit | null> => {
  try {
    const response: ApiResponse<Visit> = await apiClient.get(
      `/patientmr/${patientId}/visits/provider/active`
    );
    if (response.success && response.data) {
      return withVisitId(response.data);
    }
    throw new Error(response.error?.message || 'Failed to fetch active visit');
  } catch (error) {
    // If it's a 404 (NOT_FOUND), we'll return null to indicate no active visit
    if (error instanceof ApiError && error.code === 'NOT_FOUND') {
      return null;
    }
    console.error(`Error fetching active visit for patient ${patientId}`);
    throw error;
  }
};

/**
 * VIS-07 (Phase 9B): a coordinator closes a visit the clinician never ended.
 * PUT /patientmr/{patientId}/visits/{visitId}/close  { endTime: ISO, reason (≥10 chars) }
 */
export const closeVisit = async (patientId: string, visitId: string, endTime: string, reason: string): Promise<Visit> => {
  const response: ApiResponse<Visit> = await apiClient.put(
    `/patientmr/${encodeURIComponent(patientId)}/visits/${encodeURIComponent(visitId)}/close`,
    { endTime, reason }
  );
  if (response.success && response.data) return response.data;
  throw new Error(response.message || 'Failed to close the visit');
};

/**
 * VIS-08 (Phase 9B): correct a completed visit's times with a reason (admin / coordinator).
 * PUT /patientmr/{patientId}/visits/{visitId}/time-correction  { startTime?, endTime?, reason }
 */
export const correctVisitTime = async (
  patientId: string,
  visitId: string,
  times: { startTime?: string; endTime?: string },
  reason: string
): Promise<Visit> => {
  const response: ApiResponse<Visit> = await apiClient.put(
    `/patientmr/${encodeURIComponent(patientId)}/visits/${encodeURIComponent(visitId)}/time-correction`,
    { ...times, reason }
  );
  if (response.success && response.data) return response.data;
  throw new Error(response.message || 'Failed to correct the visit times');
};
